import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Form, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { TABLE_CONTEXT } from 'src/services/posthogEvents';

import useLocalStore from 'src/store/useLocalStore';

import ColumnConfiguration from 'src/components/custom_components/ColumnConfiguration';
import CustomTable, {
  Column,
} from 'src/components/custom_components/CustomTable/CustomTable';
import OrderStatusBadge from 'src/components/custom_components/OrderStatusBadge';
import SitesDropdown from 'src/components/custom_components/SitesDropdown';
import OrderDetailsButton from 'src/components/OrderDetailsButton';
import OrderDetailsDrawer from 'src/components/OrderDetailsDrawer';
import SearchPatientInfoFormGroup from 'src/components/SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

import { DEFAULT_PAGE_SIZE } from 'src/models/DefaultPageSize.model';
import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData, OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { I18NextNS } from 'src/models/Language.model';

import useColumnVisibility from 'src/hooks/useColumnVisibility';
import useDateFormats from 'src/hooks/useDateFormats';
import { useGetFilterOrder } from 'src/hooks/useGetFilterOrder';

const DISPLAYED_STATUSES = [
  OrderStatusEnum.PENDING,
  OrderStatusEnum.COMPLETE,
  OrderStatusEnum.CLOSED,
  OrderStatusEnum.CANCELLED,
];

const DEFAULT_COLUMN_VISIBILITY = { userEmail: false };

const TeleophthResults = () => {
  const { t } = useTranslation(I18NextNS.PAGES);
  const { t: tCommon } = useTranslation(I18NextNS.COMMON);
  const { formatDate, formatDatePart, formatTimePart } = useDateFormats();

  const [isOrderDetailsDrawerOpen, setIsOrderDetailsDrawerOpen] =
    useState(false);
  const [selectedOrder, setSelectedOrder] =
    useState<DeviceDataView<OrderData>>();

  const {
    selectedSiteId,
    teleophthIntegratedResultsColsKeys,
    setTeleophthIntegratedResultsColsKeys,
  } = useLocalStore();

  const {
    currentPage,
    handleSetCurrentPage,
    isGettingOrders: isLoading,
    dataResource,
    userEmailsByOrderId,
    handleLoadOrders,
    searchValues,
    handleSetSearchValues,
  } = useGetFilterOrder();

  useEffect(() => {
    handleLoadOrders(0, DEFAULT_PAGE_SIZE, selectedSiteId, DISPLAYED_STATUSES);
  }, [handleLoadOrders, selectedSiteId]);

  const determineUserEmail = useCallback(
    (data: DeviceDataView<OrderData>) => {
      const userEmail = userEmailsByOrderId[data.data.OrderId.value];
      if (data.data.Status.value === OrderStatusEnum.CANCELLED) {
        return tCommon('patientFields.fromCustomerEhr');
      }

      return userEmail;
    },
    [tCommon, userEmailsByOrderId],
  );

  const columns: Column<DeviceDataView<OrderData>>[] = useMemo(
    () => [
      {
        title: tCommon('patientFields.userEmail'),
        dataIndex: 'userEmail',
        render: (data: DeviceDataView<OrderData>) => determineUserEmail(data),
      },
      {
        title: tCommon('patientFields.dateTime'),
        dataIndex: 'dateTime',
        render: (data: DeviceDataView<OrderData>) => {
          const datetime = data.maxValueProvidedOn;
          if (!datetime) return null;
          return (
            <>
              <div>
                <b>{formatDatePart(datetime)}</b>
              </div>
              <div>{formatTimePart(datetime)}</div>
            </>
          );
        },
      },
      {
        title: tCommon('patientFields.First Name'),
        dataIndex: 'firstName',
        dataTest: 'result-cell-first-name',
        render: (data: DeviceDataView<OrderData>) => data.owner?.firstName,
      },
      {
        title: tCommon('patientFields.Last Name'),
        dataIndex: 'lastName',
        dataTest: 'result-cell-last-name',
        render: (data: DeviceDataView<OrderData>) => data.owner?.lastName,
      },
      {
        title: tCommon('patientFields.PID'),
        dataIndex: 'patientId',
        dataTest: 'result-cell-patient-id',
        render: (data: DeviceDataView<OrderData>) => data.owner?.patientId,
      },
      {
        title: tCommon('patientFields.DOB'),
        dataIndex: 'dob',
        dataTest: 'result-cell-dob',
        render: (data: DeviceDataView<OrderData>) =>
          formatDate(data.owner?.dateOfBirth),
      },
      {
        title: tCommon('patientFields.siteName'),
        dataIndex: 'siteName',
        dataTest: 'result-cell-site',
        render: (data: DeviceDataView<OrderData>) => data.data?.SiteName?.value,
      },
      {
        title: tCommon('orderFields.orderStatus'),
        dataIndex: 'orderStatus',
        render: (data: DeviceDataView<OrderData>) =>
          OrderStatusBadge(data.data?.Status.value),
      },
      {
        title: tCommon('orderFields.actions'),
        dataIndex: 'actions',
        render: (data: DeviceDataView<OrderData>) => (
          <OrderDetailsButton
            onClick={() => {
              setSelectedOrder(data);
              setIsOrderDetailsDrawerOpen(true);
            }}
          />
        ),
      },
    ],
    [determineUserEmail, tCommon, formatDate, formatDatePart, formatTimePart],
  );

  const { visibleColumns, handleToggleColumn, displayedColumns } =
    useColumnVisibility(
      columns,
      teleophthIntegratedResultsColsKeys,
      setTeleophthIntegratedResultsColsKeys,
      {
        defaultVisibility: DEFAULT_COLUMN_VISIBILITY,
        tableContext: TABLE_CONTEXT.RESULTS,
      },
    );

  return (
    <Stack className="h-100" gap={3}>
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-center"
        data-test="exam-results-page"
      >
        <h3 className="mb-0">{t('results.title')}</h3>
      </Stack>
      <Stack direction="horizontal" gap={3} className="align-items-end">
        <div>
          <Form.Label>{tCommon('sites.title')}</Form.Label>
          <SitesDropdown
            toggleClassName="py-2"
            handleSiteChange={(siteId) => {
              handleLoadOrders(
                0,
                DEFAULT_PAGE_SIZE,
                siteId,
                DISPLAYED_STATUSES,
                searchValues,
              );
            }}
          />
        </div>
        <SearchPatientInfoFormGroup
          onSearchValuesChange={(values) => {
            handleSetSearchValues(values);
            handleLoadOrders(
              0,
              DEFAULT_PAGE_SIZE,
              selectedSiteId,
              DISPLAYED_STATUSES,
              values,
            );
          }}
          onFormReset={() => {
            handleSetSearchValues(undefined);
            handleLoadOrders(
              0,
              DEFAULT_PAGE_SIZE,
              selectedSiteId,
              DISPLAYED_STATUSES,
            );
          }}
        />
        <ColumnConfiguration
          columns={columns}
          visibleColumns={visibleColumns}
          onToggleColumn={handleToggleColumn}
        />
      </Stack>
      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        <CustomTable
          isLoading={isLoading}
          dataSource={dataResource.content}
          rowKey="deviceDataId"
          columns={displayedColumns}
          rowProps={(record) =>
            ({
              'data-test': `result-row-${record.owner?.patientId ?? record.deviceDataId}`,
            }) as HTMLAttributes<HTMLTableRowElement>
          }
          pagination={{
            defaultPageSize: DEFAULT_PAGE_SIZE,
            pageSizeOptions: [DEFAULT_PAGE_SIZE],
            totalPages: dataResource.totalPages,
            currentPage,
            handleSetCurrentPage,
            onChangePage: (page, pageSize) => {
              handleLoadOrders(
                page - 1,
                pageSize,
                selectedSiteId,
                DISPLAYED_STATUSES,
                searchValues,
              );
            },
          }}
          empty={{ buttons: [] }}
        />
      </div>
      <OrderDetailsDrawer
        show={isOrderDetailsDrawerOpen}
        onHide={() => setIsOrderDetailsDrawerOpen(false)}
        order={selectedOrder}
        showOrderStatus={true}
      />
    </Stack>
  );
};

export default TeleophthResults;
